// authSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  ercountThunk,
  ncountThunk,
  validateTokenThunk,
} from "../thunks/authThunk";

const initialState = {
  // auth
  isLoggedIn: null,
  accessToken: null,
  accessTokenExpiresOn: null,
  refreshToken: null,
  refreshTokenExpiresOn: null,
  lIU: null,
  lIUId: null,
  role: null,
  name: null,
  empID: null,
  profileImage: null,
  userInfo: null,

  // search info
  list: [],
  recordCountFor: null,
  recordCount: null,
  pagination: null,
  totalCount: null,

  // selected row
  selectedRowKeysChildInfo: [],

  // counts
  ercount: 0,
  ncount: 0,

  // meta
  loading: false,
  error: null,

  // delegation / impersonation
  isDelegated: false,
  originalUser: null,
  actingUser: null,
  hasDelegatedAccess: false,
  givenDelegatedAccess: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     *  loginSuccess — (if you ever want to dispatch manually)
     */
    loginSuccess: (state, action) => {
      const {
        lIU,
        accessToken,
        accessTokenExpiresOn,
        refreshToken,
        refreshTokenExpiresOn,
      } = action.payload;
      state.lIU = lIU;
      state.accessToken = accessToken;
      state.accessTokenExpiresOn = accessTokenExpiresOn;
      state.refreshToken = refreshToken;
      state.refreshTokenExpiresOn = refreshTokenExpiresOn;
    },

    /**
     *  emailVerificationSuccess — (manual option)
     */
    emailVerificationSuccess: (state, action) => {
      const { isLoggedIn, lIUId, role, name, empID, profileImage, userInfo } =
        action.payload;
      state.isLoggedIn = isLoggedIn;
      state.lIUId = lIUId;
      state.role = role;
      state.name = name;
      state.empID = empID;
      state.profileImage = profileImage;
      state.userInfo = userInfo;
    },

    /**
     * 🧹 clearRedux
     */
    clearRedux: (state, action) => {
      const empID = action.payload;
      if (state.empID === empID || empID === "error") {
        return initialState;
      }
      return state;
    },

    /**
     *  searchInfo
     */
    searchInfo: (state, action) => {
      const { list, recordCountFor, recordCount, pagination, totalCount } =
        action.payload;
      state.list = list;
      state.recordCountFor = recordCountFor;
      state.recordCount = recordCount;
      state.pagination = pagination;
      state.totalCount = totalCount;
    },

    /**
     *  selectedRowInfo
     */
    selectedRowInfo: (state, action) => {
      state.selectedRowKeysChildInfo = action.payload.selectedRowKeysChildInfo;
    },

    /**
     *  ercount (manual option if needed)
     */
    ercount: (state, action) => {
      state.ercount = action.payload;
    },

    /**
     * 🔔 ncount (manual option if needed)
     */
    ncount: (state, action) => {
      state.ncount = action.payload;
    },

    startDelegation: (state, action) => {
      // Save original user only once
      if (!state.originalUser) {
        state.originalUser = {
          lIUId: state.lIUId,
          role: state.role,
          name: state.name,
          empID: state.empID,
          profileImage: state.profileImage,
          departmentID: state.departmentID,
          projectCodeID: state.projectCodeID,
          userInfo: state.userInfo,
        };
      }

      state.actingUser = action.payload;
      state.isDelegated = true;

      // Switch visible identity
      state.lIUId = action.payload.lIUId;
      state.role = action.payload.role;
      state.name = action.payload.name;
      state.empID = action.payload.empID;
      state.profileImage = action.payload.profileImage;
      state.departmentID = action.payload.departmentID;
      state.projectCodeID = action.payload.projectCodeID;
      state.userInfo = action.payload.userInfo;
    },

    stopDelegation: (state) => {
      if (!state.originalUser) return;

      state.lIUId = state.originalUser.lIUId;
      state.role = state.originalUser.role;
      state.name = state.originalUser.name;
      state.empID = state.originalUser.empID;
      state.profileImage = state.originalUser.profileImage;
      state.departmentID = state.originalUser.departmentID;
      state.projectCodeID = state.originalUser.projectCodeID;
      state.userInfo = state.originalUser.userInfo;

      state.isDelegated = false;
      state.actingUser = null;
      state.originalUser = null;
    },

    // updateUserDelegationFlags: (state, action) => {
    //   state.hasDelegatedAccess = action.payload.hasDelegatedAccess;
    //   state.givenDelegatedAccess = action.payload.givenDelegatedAccess;
    // },
  },

  extraReducers: (builder) => {
    /**
     * 🚀 validateTokenThunk
     */
    builder
      .addCase(validateTokenThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateTokenThunk.fulfilled, (state, action) => {
        state.loading = false;

        if (state.isDelegated) return;

        const { loginPayload, emailVerificationPayload } = action.payload;

        // login
        state.lIU = loginPayload.lIU;
        state.accessToken = loginPayload.accessToken;
        state.accessTokenExpiresOn = loginPayload.accessTokenExpiresOn;

        // email verification
        state.isLoggedIn = emailVerificationPayload.isLoggedIn;
        state.lIUId = emailVerificationPayload.lIUId;
        state.role = emailVerificationPayload.role;
        state.name = emailVerificationPayload.name;
        state.empID = emailVerificationPayload.empID;
        state.profileImage = emailVerificationPayload.profileImage;
        state.userInfo = emailVerificationPayload.userInfo;
      })
      .addCase(validateTokenThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Token validation failed";
      });

    /**
     * 📊 ercountThunk
     */
    builder
      .addCase(ercountThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(ercountThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.ercount = action.payload;
      })
      .addCase(ercountThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    /**
     * 🔔 ncountThunk
     */
    builder
      .addCase(ncountThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(ncountThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.ncount = action.payload;
      })
      .addCase(ncountThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  loginSuccess,
  emailVerificationSuccess,
  clearRedux,
  searchInfo,
  selectedRowInfo,
  ercount,
  ncount,
  startDelegation,
  stopDelegation,
  //updateUserDelegationFlags
} = authSlice.actions;

export default authSlice.reducer;
